import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { todoService } from '../todo/TodoService';
import { taskService } from '../todo/TaskService';
import { calendarEventService } from '../calendar/CalendarEventService';
import { workflowTemplateService } from './WorkflowTemplateService';
import notificationService from '../../features/notifications/services/NotificationService';
import messageService from '../communication/MessageService';
import { recognitionService } from '../recognitionService';

/**
 * Step types that auto-complete after entity creation (fire-and-forget).
 * These don't require human action — they execute and immediately mark completed.
 */
const AUTO_COMPLETE_TYPES = new Set(['notification', 'parent_message', 'recognition', 'calendar_event']);

/**
 * WorkflowEngineService - Starts workflow instances, creates entities, and advances steps.
 *
 * Instances are stored at schools/{schoolId}/workflowInstances/{instanceId}.
 * Each instance tracks step progress and cross-references created entities.
 *
 * Flow: startWorkflow() -> creates instance + first eligible steps' entities
 * -> onStepCompleted() -> marks step done, unblocks dependents, creates next entities
 */
class WorkflowEngineService {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.ttl = 60 * 1000; // 1 minute
  }

  _collectionRef(schoolId) {
    return collection(db, 'schools', schoolId, 'workflowInstances');
  }

  /**
   * Get all workflow instances for a school.
   */
  async getInstances(schoolId, forceRefresh = false) {
    if (!schoolId) throw new Error('schoolId is required');

    const now = Date.now();
    if (!forceRefresh && this.cache.has(schoolId) && (now - this.timestamps.get(schoolId)) < this.ttl) {
      return this.cache.get(schoolId);
    }

    try {
      const snap = await getDocs(this._collectionRef(schoolId));
      const instances = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });

      this.cache.set(schoolId, instances);
      this.timestamps.set(schoolId, now);
      return instances;
    } catch (error) {
      console.error('[WorkflowEngineService] Error fetching instances:', error);
      throw error;
    }
  }

  /**
   * Get a single workflow instance.
   */
  async getInstance(schoolId, instanceId) {
    if (!schoolId || !instanceId) throw new Error('schoolId and instanceId are required');

    try {
      const docRef = doc(db, 'schools', schoolId, 'workflowInstances', instanceId);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.error('[WorkflowEngineService] Error fetching instance:', error);
      throw error;
    }
  }

  /**
   * Start a new workflow from a template.
   *
   * @param {string} schoolId
   * @param {string} templateId
   * @param {object} context - { studentId, studentName, triggerDate, ... }
   * @param {object} actor - { uid, displayName }
   * @param {object} [staffLookup] - Map of userId -> { displayName } for assignment resolution
   */
  async startWorkflow(schoolId, templateId, context, actor, staffLookup = {}) {
    if (!schoolId || !templateId) throw new Error('schoolId and templateId are required');

    const template = await workflowTemplateService.getTemplate(schoolId, templateId);
    if (!template) throw new Error('Template not found');
    if (!template.active) throw new Error('Template is not active');

    const triggerDate = new Date();

    // Build step instances from template
    const stepInstances = template.steps
      .sort((a, b) => a.order - b.order)
      .map(step => ({
        stepId: step.id,
        name: step.name,
        type: step.type,
        config: step.config,
        timing: step.timing,
        assignment: step.assignment,
        dependsOn: step.dependsOn || [],
        status: 'pending',
        createdEntityId: null,
        createdEntityCollection: null,
        assignedTo: null,
        dueDate: null,
        createdAt: null,
        completedAt: null,
      }));

    const instance = {
      templateId,
      templateName: template.name,
      context: {
        ...context,
        triggerDate: Timestamp.fromDate(triggerDate),
      },
      status: 'active',
      stepInstances,
      totalSteps: stepInstances.length,
      completedSteps: 0,
      triggeredBy: actor.uid,
      triggeredByName: actor.displayName || '',
      triggerEvent: 'manual',
      schoolId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      completedAt: null,
    };

    try {
      const ref = await addDoc(this._collectionRef(schoolId), instance);
      const instanceId = ref.id;

      // Create entities for steps with no dependencies
      await this._createEligibleSteps(schoolId, instanceId, instance, triggerDate, actor, staffLookup);

      this._invalidateCache(schoolId);
      return { id: instanceId, ...instance };
    } catch (error) {
      console.error('[WorkflowEngineService] Error starting workflow:', error);
      throw error;
    }
  }

  /**
   * Called when a workflow-linked todo/task is completed.
   * Marks the step done, unblocks dependents, creates next entities.
   */
  async onStepCompleted(schoolId, instanceId, stepId, actor, staffLookup = {}, actionResult = null) {
    if (!schoolId || !instanceId || !stepId) return;

    try {
      const instance = await this.getInstance(schoolId, instanceId);
      if (!instance || instance.status !== 'active') return;

      const stepIndex = instance.stepInstances.findIndex(s => s.stepId === stepId);
      if (stepIndex === -1) return;

      // Mark step completed
      instance.stepInstances[stepIndex].status = 'completed';
      instance.stepInstances[stepIndex].completedAt = Timestamp.now();

      // Merge actionResult into workflow context (e.g., studentId from student picker)
      if (actionResult && typeof actionResult === 'object') {
        instance.context = { ...instance.context, ...actionResult };
      }

      const completedSteps = instance.stepInstances.filter(s => s.status === 'completed').length;
      const allDone = completedSteps === instance.totalSteps;

      const updates = {
        stepInstances: instance.stepInstances,
        completedSteps,
        context: instance.context,
        status: allDone ? 'completed' : 'active',
        updatedAt: serverTimestamp(),
      };

      if (allDone) {
        updates.completedAt = serverTimestamp();
      }

      const docRef = doc(db, 'schools', schoolId, 'workflowInstances', instanceId);
      await updateDoc(docRef, updates);

      // Create entities for newly unblocked steps
      if (!allDone) {
        const triggerDate = instance.context.triggerDate?.toDate?.() || new Date();
        await this._createEligibleSteps(schoolId, instanceId, { ...instance, ...updates }, triggerDate, actor, staffLookup);
      }

      this._invalidateCache(schoolId);
    } catch (error) {
      console.error('[WorkflowEngineService] Error advancing workflow:', error);
      throw error;
    }
  }

  /**
   * Cancel a workflow instance.
   */
  async cancelWorkflow(schoolId, instanceId) {
    if (!schoolId || !instanceId) throw new Error('schoolId and instanceId are required');

    try {
      const docRef = doc(db, 'schools', schoolId, 'workflowInstances', instanceId);
      await updateDoc(docRef, {
        status: 'cancelled',
        updatedAt: serverTimestamp(),
      });
      this._invalidateCache(schoolId);
    } catch (error) {
      console.error('[WorkflowEngineService] Error cancelling workflow:', error);
      throw error;
    }
  }

  /**
   * Find eligible (unblocked, pending) steps and create their entities.
   * Auto-complete types (notification, parent_message, recognition, calendar_event)
   * immediately mark completed and trigger a re-scan for cascading unblocks.
   */
  async _createEligibleSteps(schoolId, instanceId, instance, triggerDate, actor, staffLookup) {
    const steps = instance.stepInstances;
    const completedIds = new Set(steps.filter(s => s.status === 'completed').map(s => s.stepId));
    let changed = false;
    let autoCompleted = false;

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (step.status !== 'pending') continue;

      // Check all dependencies are completed
      const allDepsCompleted = step.dependsOn.every(depId => completedIds.has(depId));
      if (!allDepsCompleted) continue;

      // Resolve assignment
      const assignedTo = this._resolveAssignment(step.assignment, actor, staffLookup);

      // Calculate due date
      const dueDate = this._calculateDueDate(step.timing, triggerDate, steps);

      // Create the entity
      const entityResult = await this._createEntity(
        schoolId, instanceId, step, assignedTo, dueDate, actor, instance.context
      );

      if (entityResult) {
        const isAutoComplete = AUTO_COMPLETE_TYPES.has(step.type);

        steps[i] = {
          ...step,
          status: isAutoComplete ? 'completed' : 'created',
          createdEntityId: entityResult.id,
          createdEntityCollection: entityResult.collection,
          assignedTo,
          dueDate: dueDate ? Timestamp.fromDate(dueDate) : null,
          createdAt: Timestamp.now(),
          completedAt: isAutoComplete ? Timestamp.now() : null,
        };

        if (isAutoComplete) {
          completedIds.add(step.stepId);
          autoCompleted = true;
        }
        changed = true;
      }
    }

    if (changed) {
      // Update completed count
      const completedSteps = steps.filter(s => s.status === 'completed').length;
      const allDone = completedSteps === instance.totalSteps;

      const updates = {
        stepInstances: steps,
        completedSteps,
        updatedAt: serverTimestamp(),
      };

      if (allDone) {
        updates.status = 'completed';
        updates.completedAt = serverTimestamp();
      }

      const docRef = doc(db, 'schools', schoolId, 'workflowInstances', instanceId);
      await updateDoc(docRef, updates);

      // If any steps auto-completed, re-scan for newly unblocked steps (cascading)
      if (autoCompleted && !allDone) {
        await this._createEligibleSteps(
          schoolId, instanceId, { ...instance, stepInstances: steps, completedSteps }, triggerDate, actor, staffLookup
        );
      }
    }
  }

  /**
   * Resolve who a step should be assigned to.
   */
  _resolveAssignment(assignment, actor, staffLookup) {
    switch (assignment?.type) {
      case 'triggering_user':
        return actor.uid;
      case 'specific_staff':
        return assignment.staffIds?.[0] || actor.uid;
      case 'class_teacher':
        // Would need class context to resolve; falls back to actor
        return actor.uid;
      default:
        return actor.uid;
    }
  }

  /**
   * Calculate due date based on timing config.
   */
  _calculateDueDate(timing, triggerDate, steps) {
    if (!timing?.offsetDays) return null;

    let baseDate = triggerDate;

    // If offset is relative to another step's completion
    if (timing.offsetFrom?.startsWith('step:')) {
      const refStepId = timing.offsetFrom.replace('step:', '');
      const refStep = steps.find(s => s.stepId === refStepId);
      if (refStep?.completedAt) {
        baseDate = refStep.completedAt.toDate?.() || refStep.completedAt;
      }
    }

    const dueDate = new Date(baseDate);
    dueDate.setDate(dueDate.getDate() + timing.offsetDays);
    return dueDate;
  }

  /**
   * Create the actual entity (todo, task, or calendar event) for a step.
   */
  async _createEntity(schoolId, instanceId, step, assignedTo, dueDate, actor, context) {
    const title = this._interpolateTitle(step.config?.title || step.name, context);
    const description = this._interpolateTitle(step.config?.description || '', context);

    try {
      switch (step.type) {
        case 'todo': {
          const result = await todoService.createTodo(schoolId, {
            title,
            description,
            dueDate,
            relatedStudentId: context.studentId || null,
            relatedWorkflowInstanceId: instanceId,
            relatedWorkflowStepId: step.stepId,
          }, assignedTo);
          return { id: result.id, collection: 'todos' };
        }

        case 'task': {
          const result = await taskService.createTask(schoolId, {
            title,
            description,
            priority: step.config?.priority || 'normal',
            dueDate,
            assignedTo: [assignedTo],
            assignedToNames: {},
            assignedByName: actor.displayName || '',
            relatedStudentId: context.studentId || null,
            relatedStudentName: context.studentName || null,
            relatedWorkflowInstanceId: instanceId,
            relatedWorkflowStepId: step.stepId,
            // Embedded action type (select_student, issue_award, etc.)
            workflowActionType: step.config?.actionType || null,
            workflowActionConfig: step.config?.actionConfig || {},
          }, actor.uid);
          return { id: result.id, collection: 'tasks' };
        }

        case 'calendar_event': {
          const startDate = dueDate || new Date();
          const endDate = new Date(startDate);
          endDate.setHours(endDate.getHours() + (step.config?.duration || 1));

          const result = await calendarEventService.createEvent(schoolId, {
            title,
            description,
            startDate,
            endDate,
            allDay: !step.config?.duration,
            type: step.config?.eventType || 'school-event',
            relatedWorkflowInstanceId: instanceId,
            relatedWorkflowStepId: step.stepId,
          }, actor.uid);
          return { id: result.id, collection: 'calendarEvents' };
        }

        case 'notification': {
          const notifId = await notificationService.createNotification({
            type: 'workflow',
            priority: step.config?.priority || 'medium',
            category: step.config?.category || 'workflow',
            title,
            message: description,
            schoolId,
            recipientIds: assignedTo ? [assignedTo] : [],
            roleFilter: step.config?.recipientRole ? [step.config.recipientRole] : [],
            createdById: actor.uid,
            actionable: !!step.config?.actionUrl,
            actionUrl: step.config?.actionUrl || null,
            actionLabel: step.config?.actionLabel || 'View',
            metadata: {
              workflowInstanceId: instanceId,
              workflowStepId: step.stepId,
            },
          });
          return { id: notifId, collection: 'notifications' };
        }

        case 'parent_message': {
          const studentId = context.studentId;
          if (!studentId) {
            console.warn('[WorkflowEngineService] parent_message requires studentId in context');
            return null;
          }

          const parentIds = await this._getParentIdsForStudent(studentId);
          if (parentIds.length === 0) {
            console.warn('[WorkflowEngineService] No active parents found for student:', studentId);
            return null;
          }

          // Send message to first linked parent (primary contact)
          const parentId = parentIds[0];
          const msgId = await messageService.createMessage({
            schoolId,
            parentId,
            studentId,
            type: step.config?.messageType || 'general',
            priority: step.config?.messagePriority || 'normal',
            content: {
              subject: title,
              body: description,
              summary: title,
            },
            createdBy: actor.uid,
            metadata: {
              workflowInstanceId: instanceId,
              workflowStepId: step.stepId,
            },
          });
          return { id: msgId, collection: 'messages' };
        }

        case 'recognition': {
          const recStudentId = context.studentId;
          if (!recStudentId) {
            console.warn('[WorkflowEngineService] recognition requires studentId in context');
            return null;
          }

          const result = await recognitionService.issueRecognition({
            studentId: recStudentId,
            studentName: context.studentName || '',
            classId: context.classId || null,
            className: context.className || null,
            issuedById: actor.uid,
            issuedByName: actor.displayName || '',
            points: step.config?.points || 1,
            values: step.config?.values || [],
            note: description || title,
            source: 'workflow',
            schoolId,
            metadata: {
              workflowInstanceId: instanceId,
              workflowStepId: step.stepId,
            },
          });
          return { id: result.id, collection: 'recognitions' };
        }

        default:
          console.warn(`[WorkflowEngineService] Unknown step type: ${step.type}`);
          return null;
      }
    } catch (error) {
      console.error(`[WorkflowEngineService] Error creating ${step.type} entity:`, error);
      return null;
    }
  }

  /**
   * Simple template interpolation: replaces {key} with context values.
   */
  _interpolateTitle(template, context) {
    if (!template) return '';
    return template.replace(/\{(\w+)\}/g, (match, key) => context[key] || match);
  }

  /**
   * Look up active parent IDs linked to a student via parentChildLinks.
   */
  async _getParentIdsForStudent(studentId) {
    try {
      const q = query(
        collection(db, 'parentChildLinks'),
        where('studentId', '==', studentId),
        where('status', '==', 'active')
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => d.data().parentId).filter(Boolean);
    } catch (error) {
      console.error('[WorkflowEngineService] Error fetching parent IDs:', error);
      return [];
    }
  }

  _invalidateCache(schoolId) {
    this.cache.delete(schoolId);
    this.timestamps.delete(schoolId);
  }
}

export const workflowEngineService = new WorkflowEngineService();
export default workflowEngineService;